import { useState, useCallback, useEffect } from "react";
import { supabase } from "../lib/supabase";

export interface PassengerData {
  year: string;
  value: number;
  label: string;
}

export interface CargoData {
  year: string;
  value: number;
  label: string;
}

export interface TopCountry {
  country: string;
  flag: string;
  share: number;
  passengers: string;
  trend: string;
}

export interface TopRoute {
  route: string;
  airline: string;
  freq: string;
  pax: string;
}

export interface SeasonalData {
  month: string;
  value: number;
}

export interface SectorStats {
  id: number;
  pasajeros_value: string;
  pasajeros_trend: string;
  operaciones_value: string;
  operaciones_trend: string;
  carga_value: string;
  carga_trend: string;
  ocupacion_value: string;
  ocupacion_trend: string;
  passenger_data: PassengerData[];
  cargo_data: CargoData[];
  top_countries: TopCountry[];
  top_routes: TopRoute[];
  seasonal_data: SeasonalData[];
  data_source: string;
  data_source_date: string;
  updated_at: string;
}

export function useSectorStats() {
  const [stats, setStats] = useState<SectorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError("");
    const { data, error: fetchError } = await supabase
      .from("sector_stats")
      .select("*")
      .eq("id", 1)
      .maybeSingle();

    if (fetchError) {
      setError("Error al cargar las estadísticas.");
    } else if (data) {
      setStats(data as SectorStats);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const updateStats = async (payload: Partial<Omit<SectorStats, "id" | "updated_at">>): Promise<void> => {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;

    const functionUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-sector-stats`;

    const res = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    const result = await res.json();

    if (!res.ok) {
      throw new Error(result.error || "Error al actualizar las estadísticas");
    }

    await fetchStats();
  };

  return { stats, loading, error, fetchStats, updateStats };
}